import { useState } from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  CompensationsMergeConceptDocument,
  CompensationsPayrollConceptFragment,
  CompensationsPayrollConceptsDocument,
} from 'generated/resources/graphql'
import { PayrollConceptSelectFieldFragment } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { PayrollConceptSelect } from 'modules/compensations/components/PayrollConceptSelectField'
import { useCategorizedPayrollConcepts } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'

interface Props {
  concept: CompensationsPayrollConceptFragment
  onSuccess: () => void
  onClose: () => void
}

const MergeConceptModal = ({ concept, onSuccess, onClose }: Props) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const [selectedPayrollConceptId, setSelectedPayrollConceptId] = useState<number | undefined>()

  const [merge] = useMutation(CompensationsMergeConceptDocument, {
    extractorFn: (result) => result.compensationsMutations.mergeCompensationsConcept,
  })

  const { data } = useQuery(CompensationsPayrollConceptsDocument, {
    variables: {
      companyId,
    },
  })

  const allConcepts = data?.payroll.concepts || []
  const filteredConcepts: PayrollConceptSelectFieldFragment[] = allConcepts
    .filter((c) => c.id !== concept.id && c.unitType === concept.unitType)
    .map((c) => ({
      id: c.id,
      category: c.category,
      label: c.label,
      name: c.name,
      unitType: c.unitType,
    }))
  const payrollConceptsOptions = useCategorizedPayrollConcepts(filteredConcepts)

  const handleMerge = async () => {
    if (!selectedPayrollConceptId) {
      return
    }

    const result = await merge({
      variables: {
        fromPayrollConceptId: concept.id,
        toPayrollConceptId: selectedPayrollConceptId,
      },
      refetchQueries: [CompensationsPayrollConceptsDocument],
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onSuccess()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }
  }

  return (
    <Modal
      illustration="edit-file"
      title={i18n.t('compensations.settings.concepts.merge_modal.title', {
        conceptLabel: concept.label,
      })}
      description={i18n.t('compensations.settings.concepts.merge_modal.description')}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={handleMerge} disabled={!selectedPayrollConceptId}>
            {i18n.t('common.confirm')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <Callout type="info">
          <Box flexDirection="row" gap="s8" alignItems="flexStart">
            <Icon.Medium icon="lightbulb" />
            <Box gap="s4">
              <Text.Normal color="grey1000" weight="medium">
                {i18n.t('compensations.settings.concepts.merge_modal.what_will_happen')}
              </Text.Normal>
              <Text.Normal color="grey1000">
                {i18n.t('compensations.settings.concepts.merge_modal.explanation', {
                  fromConcept: concept.label,
                })}
              </Text.Normal>
            </Box>
          </Box>
        </Callout>
        <Box gap="s8">
          <Text.Normal color="grey1000" weight="medium">
            {i18n.t('compensations.settings.concepts.merge_modal.select_label')}
          </Text.Normal>
          <PayrollConceptSelect
            payrollConcepts={payrollConceptsOptions}
            value={selectedPayrollConceptId ? [selectedPayrollConceptId] : []}
            onChange={(payrollConceptId) => {
              setSelectedPayrollConceptId(
                typeof payrollConceptId === 'number' ? payrollConceptId : undefined
              )
            }}
          />
        </Box>
      </Box>
    </Modal>
  )
}

export default MergeConceptModal
